import {ConfigObjectType, enumContainer} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectedFiltersPane,
  LightSelectFilter,
  NormalizedOption,
  ShortcutEnum,
  TooltipWrapper,
  useFormatters,
  UserFilterDto,
  useOrganizations,
} from '@wallaby-ui/document';
import classNames from 'classnames';
import {sortBy} from 'lodash';
import * as React from 'react';
import {useCallback} from 'react';

import {FiltersData, UzisReportFilter} from './uzisreporttypes';
import {useAppSelector} from '../../../store_hooks';

type Props = {
  filters: UzisReportFilter;
  onFiltersChange: (newFilters: Partial<UzisReportFilter>) => void;
  savedFiltersOpen: boolean;
  onToggleSavedFilters: () => void;
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  config: ConfigObjectType;
  onSetFilter: (filter: UzisReportFilter) => void;
  defaultReportFilters?: FiltersData['report'];
  hasActiveFilter?: boolean;
};
export function normalizeUzisReportFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete copy.dateFrom;
    delete copy.dateTo;
  }
  return copy;
}
export const UzisReportFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  savedFiltersOpen,
  onToggleSavedFilters,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  config,
  onSetFilter,
  defaultReportFilters,
  hasActiveFilter,
}) => {
  const formatters = useFormatters();
  const {t, formatDate, tEnum} = formatters;
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const {data: organizations} = useOrganizations(config);

  const handleDateChange = useCallback(
    (newValue: {startDate?: string; endDate?: string; shortcut?: ShortcutEnum | null} | null) => {
      onFiltersChange({
        dateFrom: newValue?.startDate ?? null,
        dateTo: newValue?.endDate ?? null,
        floatingShortcutName: newValue?.shortcut ?? null,
      });
    },
    [onFiltersChange]
  );

  const handleOrganizationIdsChange = useCallback(
    (newValue: string[]) => {
      onFiltersChange({organizationIds: newValue as any});
    },
    [onFiltersChange]
  );

  const handleCalculationMethodChange = useCallback(
    (newValue: any) => {
      onFiltersChange({includeDrugChanges: newValue});
    },
    [onFiltersChange]
  );

  const handleSetFilter = useCallback(
    (newFilter: Record<string, any>) => {
      onSetFilter(newFilter as UzisReportFilter);
    },
    [onSetFilter]
  );

  const sortByMessageKey = useCallback((options: any[]) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const getDrugChangesTooltip = useCallback(
    (id: any) => (id === 'EXCLUDE_DRUG_CHANGES' ? t('reports.uzis.filters.method.exclude') : t('reports.uzis.filters.method.include')),
    [t]
  );
  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('hospital.network.report.filter.date'),
          value: `${formatDate(filters.dateFrom ?? '')} - ${formatDate(filters.dateTo ?? '')}`,
          defaultValue: [defaultReportFilters?.dateFrom, defaultReportFilters?.dateTo, defaultReportFilters?.floatingShortcutName],
        },
        {
          name: 'organizationIds',
          label: t('hospital.report.filter.departmentdetail.department'),
          value: (filters.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName ?? String(id))
            .join(', '),
        },
        {
          name: 'includeDrugChanges',
          label: t('hospital.network.report.filter.aggregation'),
          value: tEnum('UzisReportIncludeDrugChanges', filters.includeDrugChanges ?? undefined),
          defaultValue: defaultReportFilters?.includeDrugChanges,
        },
      ]}
      tableFilters={filters as Record<string, any>}
      changeTableFilters={(newTableFilters) => onFiltersChange(newTableFilters as Partial<UzisReportFilter>)}
      formatters={formatters}
    />
  );
  return (
    <LightFilterPanel
      open={savedFiltersOpen}
      toggleOpen={onToggleSavedFilters}
      hasActiveFilter={hasActiveFilter}
      savedFiltersProps={{
        storageIdPrefix,
        component,
        userFiltersLoading,
        addedUserFilters,
        userFilters,
        changeUserFilter,
        filter: filters as Record<string, any>,
        setFilter: handleSetFilter,
        config,
        normalizeForCompare: normalizeUzisReportFiltersForCompare,
      }}
      selectedFilters={selectedFiltersPane}
    >
      <LightMonthRangeFilter
        config={{
          name: 'reportDate',
          ui: {label: 'hospital.network.report.filter.date'},
        }}
        value={
          filters.dateFrom && filters.dateTo
            ? {
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                shortcut: filters.floatingShortcutName,
              }
            : null
        }
        defaultValue={
          defaultReportFilters?.dateFrom && defaultReportFilters?.dateTo
            ? {
                startDate: defaultReportFilters?.dateFrom,
                endDate: defaultReportFilters?.dateTo,
                shortcut: defaultReportFilters?.floatingShortcutName,
              }
            : null
        }
        onChange={handleDateChange}
        isRemoveFilterButtonHidden
        showShortcuts
      />
      <LightOrganizationFilter2
        appConfig={config}
        config={{
          name: 'organizationIds',
          ui: {
            label: 'hospital.report.filter.departmentdetail.department',
          },
        }}
        multi
        value={filters.organizationIds}
        onChange={handleOrganizationIdsChange}
      />
      <LightSelectFilter
        config={{
          name: 'calculationMethod',
          ui: {label: 'hospital.network.report.filter.aggregation'},
        }}
        options={sortByMessageKey(enumMap.UzisReportIncludeDrugChanges ?? [])}
        maxWidth='362px'
        value={filters.includeDrugChanges}
        onChange={handleCalculationMethodChange}
        isClearable={false}
        renderOption={(props, option) => {
          const {id, label} = option as NormalizedOption;
          const tooltip = getDrugChangesTooltip(id);
          // Merge in `light-select-filter-option` (the class the default renderOption uses)
          // so the selected/focused option gets MERIE's teal highlight from datlowe-core.less
          // instead of MUI's default light-blue.
          const className = classNames(props.className, 'light-select-filter-option');
          return (
            <li {...props} className={className} style={{width: '100%'}}>
              <TooltipWrapper tooltip={tooltip} style={{width: '100%'}}>
                <span style={{display: 'inline-block', paddingLeft: '3px', width: '100%'}}>{label}</span>
              </TooltipWrapper>
            </li>
          );
        }}
      />
    </LightFilterPanel>
  );
};
